// historyRecapImage.js - Build a shareable PNG recap for a history chart share.

const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1350;
const TOP_SAFE = 12;
const FOOTER_HEIGHT = 100;
const PISTA_GREEN = '#2E7D32';

const LUCIDE_ICON_SVGS = {
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  dices: '<rect x="2" y="10" width="12" height="12" rx="2" ry="2"/><path d="m17.92 14 3.5-3.5a2.24 2.24 0 0 0 0-3l-5-4.92a2.24 2.24 0 0 0-3 0L10 6"/><path d="M6 18h.01"/><path d="M10 14h.01"/><path d="M15 6h.01"/><path d="M18 9h.01"/>',
  timer: '<line x1="10" y1="2" x2="14" y2="2"/><line x1="12" y1="14" x2="15" y2="11"/><circle cx="12" cy="14" r="8"/>',
};

function utf8ToBase64(str) {
  // eslint-disable-next-line no-undef
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) {
    bin += String.fromCharCode(bytes[i]);
  }
  // eslint-disable-next-line no-undef
  return btoa(bin);
}

function getLucideIconDataUrl(name, color = '#ffffff') {
  const body = LUCIDE_ICON_SVGS[name];
  if (!body) return null;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
  return 'data:image/svg+xml;base64,' + utf8ToBase64(svg);
}

function loadImage(fileOrUrl) {
  return new Promise((resolve) => {
    if (!fileOrUrl) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    const timeout = setTimeout(() => resolve(null), 5000);
    img.onload = () => {
      clearTimeout(timeout);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(null);
    };

    if (typeof fileOrUrl === 'string') {
      img.src = fileOrUrl;
    } else if (fileOrUrl instanceof File || fileOrUrl instanceof Blob) {
      img.src = URL.createObjectURL(fileOrUrl);
      img.onload = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(img.src);
        resolve(img);
      };
      img.onerror = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(img.src);
        resolve(null);
      };
    } else {
      resolve(null);
    }
  });
}

export async function buildHistoryRecapImage(chartImageFile, stats, options = {}) {
  const {
    userName = 'Pista User',
    filename = 'history-recap.png',
    backgroundColor = '#fafbfc',
    rangeText,
  } = options;

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  const chartImg = await loadImage(chartImageFile);

  // Draw the chart with contain scaling so it isn't cropped or zoomed in.
  const topPadding = 12;
  const gap = 16;
  const availableWidth = CANVAS_WIDTH - 40;
  const availableHeight = CANVAS_HEIGHT - topPadding - FOOTER_HEIGHT - gap;

  if (chartImg) {
    const scale = Math.min(availableWidth / chartImg.width, availableHeight / chartImg.height);
    const chartWidth = chartImg.width * scale;
    const chartHeight = chartImg.height * scale;
    const chartX = (CANVAS_WIDTH - chartWidth) / 2;
    const chartY = topPadding + (availableHeight - chartHeight) / 2;

    ctx.drawImage(chartImg, chartX, chartY, chartWidth, chartHeight);
  } else {
    ctx.fillStyle = '#e0e0e0';
    ctx.beginPath();
    ctx.roundRect(40, topPadding, availableWidth, availableHeight, 20);
    ctx.fill();
  }

  // Pista-green footer anchored to the right under the chart.
  const footerY = CANVAS_HEIGHT - FOOTER_HEIGHT;
  ctx.fillStyle = PISTA_GREEN;
  ctx.beginPath();
  ctx.roundRect(0, footerY, CANVAS_WIDTH, FOOTER_HEIGHT, [16, 16, 0, 0]);
  ctx.fill();

  // Range label at the top-right so the chart has context.
  if (rangeText) {
    ctx.save();
    ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 4;
    ctx.fillText(rangeText, CANVAS_WIDTH - 20, TOP_SAFE);
    ctx.restore();
  }

  // Stats on the right side of the footer, in white.
  const statLabels = [
    { label: 'sessions', value: stats.sessions || 0, icon: 'users' },
    { label: 'games', value: stats.games || 0, icon: 'dices' },
    { label: 'hours', value: stats.hours || 0, icon: 'timer' },
  ];
  const cardWidth = 90;
  const statGap = 20;
  const statY = footerY + 16;
  const totalStatsWidth = statLabels.length * cardWidth + (statLabels.length - 1) * statGap;
  const startX = CANVAS_WIDTH - 24 - totalStatsWidth;

  const [usersIcon, dicesIcon, timerIcon] = await Promise.all([
    loadImage(getLucideIconDataUrl('users', '#ffffff')),
    loadImage(getLucideIconDataUrl('dices', '#ffffff')),
    loadImage(getLucideIconDataUrl('timer', '#ffffff')),
  ]);
  const iconMap = { users: usersIcon, dices: dicesIcon, timer: timerIcon };

  statLabels.forEach((stat, index) => {
    const x = startX + index * (cardWidth + statGap);

    ctx.textBaseline = 'top';
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 22px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    const valueText = typeof stat.value === 'number' ? stat.value.toLocaleString() : String(stat.value);
    const iconImg = iconMap[stat.icon];

    if (iconImg) {
      const valueWidth = ctx.measureText(valueText).width;
      const iconSize = 18;
      const gap = 6;
      const groupWidth = valueWidth + iconSize + gap;
      const groupLeft = x + (cardWidth - groupWidth) / 2;
      ctx.drawImage(iconImg, groupLeft, statY + 2, iconSize, iconSize);
      ctx.textAlign = 'left';
      ctx.fillText(valueText, groupLeft + iconSize + gap, statY + 2);
    } else {
      ctx.textAlign = 'center';
      ctx.fillText(valueText, x + cardWidth / 2, statY);
    }

    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText(stat.label, x + cardWidth / 2, statY + 30);
  });

  // Footer: username on the left, brand mark on the right, both in white.
  ctx.fillStyle = '#ffffff';
  ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  ctx.fillText(userName, 24, CANVAS_HEIGHT - 18);
  ctx.textAlign = 'right';
  ctx.fillText('Pista', CANVAS_WIDTH - 24, CANVAS_HEIGHT - 18);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve(null);
        return;
      }
      resolve(new File([blob], filename, { type: 'image/png' }));
    }, 'image/png');
  });
}

export default buildHistoryRecapImage;
